/**
 * 백엔드 ErrorCode와 동기화된 에러코드 정의
 *
 * 코드 규칙:
 * - AUTH_xxx: 인증 관련 에러 (1xx)
 * - BRAND_xxx: 브랜드 관련 에러 (2xx)
 * - PROJECT_xxx: 프로젝트 관련 에러 (3xx)
 * - SAMPLE_xxx: 샘플 요청 관련 에러 (4xx)
 * - SHIPPING_xxx: 배송 관련 에러 (5xx)
 * - FILE_xxx: 파일 관련 에러 (6xx)
 * - COMMON_xxx: 공통 에러 (9xx)
 */

// 에러 코드 상수
export const ErrorCode = {
  // ==================== 인증 관련 에러 (1xx) ====================
  AUTH_INVALID_TOKEN: 'AUTH_101',
  AUTH_INVALID_CREDENTIALS: 'AUTH_102',
  AUTH_TOKEN_EXPIRED: 'AUTH_103',
  AUTH_VERIFICATION_CODE_EXPIRED: 'AUTH_104',
  AUTH_VERIFICATION_CODE_INVALID: 'AUTH_105',
  AUTH_ACCOUNT_DEACTIVATED: 'AUTH_106',
  AUTH_EMAIL_NOT_VERIFIED: 'AUTH_107',
  AUTH_ACCESS_DENIED: 'AUTH_108',
  AUTH_REGISTRATION_TOKEN_EXPIRED: 'AUTH_109',

  // ==================== 브랜드 관련 에러 (2xx) ====================
  BRAND_NOT_FOUND: 'BRAND_201',
  BRAND_EMAIL_DUPLICATE: 'BRAND_202',
  BRAND_ALREADY_ASSIGNED: 'BRAND_203',
  BRAND_CREATE_FAILED: 'BRAND_204',
  BRAND_UPDATE_FAILED: 'BRAND_205',
  BRAND_DEACTIVATE_FAILED: 'BRAND_206',

  // ==================== 프로젝트 관련 에러 (3xx) ====================
  PROJECT_NOT_FOUND: 'PROJECT_301',
  PROJECT_NAME_DUPLICATE: 'PROJECT_302',
  PROJECT_CIRCULAR_REFERENCE: 'PROJECT_303',
  PROJECT_SELF_PARENT: 'PROJECT_304',
  PROJECT_HAS_CHILDREN: 'PROJECT_305',
  PROJECT_ACCESS_DENIED: 'PROJECT_306',
  PROJECT_ALREADY_ASSIGNED: 'PROJECT_307',

  // ==================== 샘플 요청 관련 에러 (4xx) ====================
  SAMPLE_REQUEST_NOT_FOUND: 'SAMPLE_401',
  SAMPLE_REQUEST_CREATE_FAILED: 'SAMPLE_402',
  SAMPLE_REQUEST_UPDATE_FAILED: 'SAMPLE_403',
  SAMPLE_REQUEST_DELETE_FAILED: 'SAMPLE_404',
  SAMPLE_REQUEST_INVALID_STATUS: 'SAMPLE_405',
  SAMPLE_REQUEST_ACCESS_DENIED: 'SAMPLE_406',
  SAMPLE_REQUEST_BRAND_REQUIRED: 'SAMPLE_407',
  SAMPLE_REQUEST_ONLY_DRAFT_SUBMIT: 'SAMPLE_408',
  SAMPLE_REQUEST_ONLY_DRAFT_DELETE: 'SAMPLE_409',
  SAMPLE_REQUEST_ALREADY_CANCELLED: 'SAMPLE_410',
  SAMPLE_REQUEST_DRAFT_CANNOT_CANCEL: 'SAMPLE_411',
  SAMPLE_REQUEST_VERSION_NOT_FOUND: 'SAMPLE_412',
  SAMPLE_REQUEST_DUPLICATE_SUBMISSION: 'SAMPLE_413',

  // ==================== 배송 관련 에러 (5xx) ====================
  SHIPPING_NOT_FOUND: 'SHIPPING_501',
  SHIPPING_TRACKING_NUMBER_REQUIRED: 'SHIPPING_502',
  SHIPPING_CREATE_FAILED: 'SHIPPING_503',
  SHIPPING_UPDATE_FAILED: 'SHIPPING_504',

  // ==================== 파일 관련 에러 (6xx) ====================
  FILE_NOT_FOUND: 'FILE_601',
  FILE_UPLOAD_REQUIRED: 'FILE_602',
  FILE_TOO_LARGE: 'FILE_603',
  FILE_INVALID_TYPE: 'FILE_604',
  FILE_PROCESS_FAILED: 'FILE_605',

  // ==================== 공통 에러 (9xx) ====================
  VALIDATION_ERROR: 'COMMON_901',
  INVALID_ARGUMENT: 'COMMON_902',
  RESOURCE_NOT_FOUND: 'COMMON_903',
  DUPLICATE_RESOURCE: 'COMMON_904',
  INTERNAL_ERROR: 'COMMON_999',
} as const;

export type ErrorCodeType = typeof ErrorCode[keyof typeof ErrorCode];

// 에러 액션 타입 정의
export type ErrorAction =
  | { type: 'redirect'; path: string }
  | { type: 'refresh' }
  | { type: 'modal'; modalType: string }
  | { type: 'retry' }
  | { type: 'none' };

// 에러 정보 인터페이스
export interface ErrorInfo {
  code: string;
  message: string;
  userMessage: string;
  action: ErrorAction;
  severity: 'info' | 'warning' | 'error';
}

// 사용자 친화적 에러 메시지 매핑
const errorMessages: Record<string, Omit<ErrorInfo, 'code'>> = {
  // 인증 관련
  [ErrorCode.AUTH_INVALID_TOKEN]: {
    message: 'Invalid or expired token',
    userMessage: 'Your session has expired. Please sign in again.',
    action: { type: 'redirect', path: '/login' },
    severity: 'warning',
  },
  [ErrorCode.AUTH_INVALID_CREDENTIALS]: {
    message: 'Invalid email or password',
    userMessage: 'The email or password you entered is incorrect. Please try again.',
    action: { type: 'none' },
    severity: 'error',
  },
  [ErrorCode.AUTH_TOKEN_EXPIRED]: {
    message: 'Token has expired',
    userMessage: 'Your session has expired. Refreshing...',
    action: { type: 'refresh' },
    severity: 'info',
  },
  [ErrorCode.AUTH_VERIFICATION_CODE_EXPIRED]: {
    message: 'Verification code has expired',
    userMessage: 'The verification code has expired. Please request a new one.',
    action: { type: 'retry' },
    severity: 'warning',
  },
  [ErrorCode.AUTH_VERIFICATION_CODE_INVALID]: {
    message: 'Invalid verification code',
    userMessage: 'The verification code you entered is invalid. Please check and try again.',
    action: { type: 'none' },
    severity: 'error',
  },
  [ErrorCode.AUTH_ACCOUNT_DEACTIVATED]: {
    message: 'Account is deactivated',
    userMessage: 'Your account has been deactivated. Please contact support for assistance.',
    action: { type: 'none' },
    severity: 'error',
  },
  [ErrorCode.AUTH_EMAIL_NOT_VERIFIED]: {
    message: 'Email not verified',
    userMessage: 'Please verify your email before continuing.',
    action: { type: 'modal', modalType: 'verifyEmail' },
    severity: 'warning',
  },
  [ErrorCode.AUTH_ACCESS_DENIED]: {
    message: 'Access denied',
    userMessage: 'You do not have permission to access this resource.',
    action: { type: 'none' },
    severity: 'error',
  },
  [ErrorCode.AUTH_REGISTRATION_TOKEN_EXPIRED]: {
    message: 'Registration token expired',
    userMessage: 'Your registration link has expired. Please verify your email again.',
    action: { type: 'redirect', path: '/login' },
    severity: 'warning',
  },

  // 브랜드 관련
  [ErrorCode.BRAND_NOT_FOUND]: {
    message: 'Brand not found',
    userMessage: 'The brand account was not found. Please sign up first.',
    action: { type: 'redirect', path: '/login' },
    severity: 'warning',
  },
  [ErrorCode.BRAND_EMAIL_DUPLICATE]: {
    message: 'Email already registered',
    userMessage: 'This email is already registered. Please sign in or use a different email.',
    action: { type: 'redirect', path: '/login' },
    severity: 'info',
  },
  [ErrorCode.BRAND_ALREADY_ASSIGNED]: {
    message: 'Brand is already assigned',
    userMessage: 'This brand is already assigned to another manager.',
    action: { type: 'none' },
    severity: 'warning',
  },
  [ErrorCode.BRAND_CREATE_FAILED]: {
    message: 'Failed to create brand',
    userMessage: 'Could not create your account. Please try again later.',
    action: { type: 'retry' },
    severity: 'error',
  },

  // 프로젝트 관련
  [ErrorCode.PROJECT_NOT_FOUND]: {
    message: 'Project not found',
    userMessage: 'The project you are looking for does not exist.',
    action: { type: 'none' },
    severity: 'error',
  },
  [ErrorCode.PROJECT_NAME_DUPLICATE]: {
    message: 'Project with same name already exists',
    userMessage: 'A project with this name already exists. Please use a different name.',
    action: { type: 'none' },
    severity: 'warning',
  },
  [ErrorCode.PROJECT_CIRCULAR_REFERENCE]: {
    message: 'Cannot set child project as parent',
    userMessage: 'You cannot move a project into its own child folder.',
    action: { type: 'none' },
    severity: 'warning',
  },
  [ErrorCode.PROJECT_HAS_CHILDREN]: {
    message: 'Project has child projects',
    userMessage: 'Please delete or move child projects before deleting this project.',
    action: { type: 'none' },
    severity: 'warning',
  },
  [ErrorCode.PROJECT_ACCESS_DENIED]: {
    message: 'No permission to access this project',
    userMessage: 'You do not have permission to access this project.',
    action: { type: 'none' },
    severity: 'error',
  },

  // 샘플 요청 관련
  [ErrorCode.SAMPLE_REQUEST_NOT_FOUND]: {
    message: 'Sample request not found',
    userMessage: 'The sample request you are looking for does not exist.',
    action: { type: 'none' },
    severity: 'error',
  },
  [ErrorCode.SAMPLE_REQUEST_CREATE_FAILED]: {
    message: 'Failed to create sample request',
    userMessage: 'Could not create the sample request. Please try again.',
    action: { type: 'retry' },
    severity: 'error',
  },
  [ErrorCode.SAMPLE_REQUEST_ACCESS_DENIED]: {
    message: 'No permission to access this sample request',
    userMessage: 'You do not have permission to view or edit this sample request.',
    action: { type: 'none' },
    severity: 'error',
  },
  [ErrorCode.SAMPLE_REQUEST_VERSION_NOT_FOUND]: {
    message: 'Version not found',
    userMessage: 'Version history is not available for this request.',
    action: { type: 'none' },
    severity: 'info',
  },

  // 파일 관련
  [ErrorCode.FILE_NOT_FOUND]: {
    message: 'File not found',
    userMessage: 'The requested file could not be found.',
    action: { type: 'none' },
    severity: 'error',
  },
  [ErrorCode.FILE_TOO_LARGE]: {
    message: 'File size exceeds maximum limit',
    userMessage: 'The file is too large. Please upload a smaller file (max 10MB).',
    action: { type: 'none' },
    severity: 'warning',
  },
  [ErrorCode.FILE_INVALID_TYPE]: {
    message: 'Invalid file type',
    userMessage: 'This file type is not supported. Please upload a valid file.',
    action: { type: 'none' },
    severity: 'warning',
  },

  // 공통 에러
  [ErrorCode.VALIDATION_ERROR]: {
    message: 'Validation failed',
    userMessage: 'Please check your input and try again.',
    action: { type: 'none' },
    severity: 'warning',
  },
  [ErrorCode.RESOURCE_NOT_FOUND]: {
    message: 'Resource not found',
    userMessage: 'The requested resource could not be found.',
    action: { type: 'none' },
    severity: 'error',
  },
  [ErrorCode.INTERNAL_ERROR]: {
    message: 'An unexpected error occurred',
    userMessage: 'Something went wrong. Please try again later.',
    action: { type: 'retry' },
    severity: 'error',
  },
};

// 기본 에러 정보
const defaultErrorInfo: Omit<ErrorInfo, 'code'> = {
  message: 'An error occurred',
  userMessage: 'Something went wrong. Please try again.',
  action: { type: 'none' },
  severity: 'error',
};

/**
 * 에러코드에서 ErrorInfo 추출
 */
export function getErrorInfo(code: string | undefined | null): ErrorInfo {
  if (!code) {
    return { code: 'UNKNOWN', ...defaultErrorInfo };
  }

  const info = errorMessages[code];
  if (info) {
    return { code, ...info };
  }

  return { code, ...defaultErrorInfo };
}

/**
 * API 응답에서 에러 정보 추출
 *
 * 백엔드 ApiResponse 구조 지원:
 * - 코드형: { error: { code: "SAMPLE_402", message: "..." } }
 * - 비코드형: { error: { message: "..." } }
 * - 레거시: { code: "...", message: "..." }
 */
export function extractErrorFromResponse(error: any): ErrorInfo {
  const responseData = error?.response?.data;

  // 에러 코드 추출: error.code (nested) → data.code (legacy)
  const errorObj = responseData?.error;
  const errorCode = (typeof errorObj === 'object' && errorObj?.code)
    ? errorObj.code
    : (typeof errorObj === 'string' ? errorObj : responseData?.code);

  // 서버 메시지 추출: error.message (nested) → data.message (legacy)
  const serverMessage = (typeof errorObj === 'object' && errorObj?.message)
    ? errorObj.message
    : responseData?.message;

  const errorInfo = getErrorInfo(errorCode);

  // 서버 메시지가 있으면 사용자 메시지 업데이트
  if (serverMessage && serverMessage !== errorInfo.message) {
    return {
      ...errorInfo,
      userMessage: serverMessage,
    };
  }

  return errorInfo;
}

/**
 * 에러가 인증 관련인지 확인
 */
export function isAuthError(code: string): boolean {
  return code.startsWith('AUTH_');
}

/**
 * 에러가 로그인 필요한 에러인지 확인
 */
export function requiresLogin(code: string): boolean {
  return [
    ErrorCode.AUTH_INVALID_TOKEN,
    ErrorCode.AUTH_TOKEN_EXPIRED,
    ErrorCode.AUTH_ACCESS_DENIED,
    ErrorCode.BRAND_NOT_FOUND,
  ].includes(code as any);
}

/**
 * 에러가 토큰 갱신으로 해결 가능한지 확인
 */
export function canRefreshToken(code: string): boolean {
  return code === ErrorCode.AUTH_TOKEN_EXPIRED;
}
